import { LoginForm, type RequestCodeOutcome, type VerifyCodeOutcome } from "@teamtable/portal-ui";
import { useEffect } from "react";
import { useLocation, useSearch } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import {
  rememberReturnTo,
  resolveExternalReturnTo,
  takeRememberedReturnTo,
} from "../lib/returnTo.js";

export function Login({ returnTo }: { returnTo?: string }) {
  const [, navigate] = useLocation();
  const search = useSearch();
  const { refresh } = useAuth();

  const externalReturnTo = resolveExternalReturnTo(search);

  /** Stashed so the emailed link, which arrives without the query parameter, can still honour it. */
  useEffect(() => {
    if (externalReturnTo) rememberReturnTo(externalReturnTo);
  }, [externalReturnTo]);

  async function requestCode(email: string): Promise<RequestCodeOutcome> {
    try {
      const res = await apiClient["/api/v1/admin/auth/request-code"]["post"]({
        parameters: {},
        body: { email },
      });
      // The client hands back undeclared status codes verbatim rather than
      // throwing, so every status is named here and anything unnamed is a
      // server fault, not a bad address.
      if (res.code === 200) return "sent";
      if (res.code === 400) return "invalid-email";
      if (res.code === 429) return "throttled";
      return "error";
    } catch {
      return "error";
    }
  }

  async function verifyCode(email: string, code: string): Promise<VerifyCodeOutcome> {
    try {
      const res = await apiClient["/api/v1/admin/auth/verify-code"]["post"]({
        parameters: {},
        body: { email, code },
      });
      if (res.code === 401) return "invalid";
      if (res.code !== 200) return "error";
      await refresh();
      /**
       * Consume the stored copy on every successful sign-in, not just the redirecting one.
       * Leaving it behind would send a later magic-link sign-in to a destination from a previous
       * visit.
       */
      const stored = takeRememberedReturnTo();

      /** A sibling TeamTable app sent them here; hand them back with a real navigation. */
      const destination = externalReturnTo ?? stored;
      if (destination) {
        window.location.assign(destination);
        return "ok";
      }
      navigate(returnTo ?? "/");
      return "ok";
    } catch {
      return "error";
    }
  }

  return (
    <LoginForm
      title="TeamTable Admin"
      subtitle="Sign in to access the admin dashboard."
      onRequestCode={requestCode}
      onVerifyCode={verifyCode}
    />
  );
}
